/**
 * Mini-SVG glyph voor een ArchiMate-relatie. Gebruikt de canonical
 * `RELATION_STYLES` uit canvas/edgeStyles zodat het uiterlijk in de
 * Explorer-detail-tabel matcht met wat op de canvas getoond wordt:
 * - lijn-stijl: dotted (Realization/Access), dashed (Flow/Influence),
 *   solid (rest)
 * - markers: filled diamond, open diamond, filled arrow, open arrow,
 *   filled circle, open triangle, half-arrow — afhankelijk van het
 *   relation-type en eventueel zijn attributen (Access.access_kind,
 *   Association.directed).
 *
 * `reversed` flipt de glyph horizontaal — gebruik 't wanneer het
 * "huidige" concept aan de target-kant van de relatie staat zodat
 * de pijl optisch wijst naar het andere endpoint.
 */

import type { EdgeMarker } from "@xyflow/react";
import { MarkerType } from "@xyflow/react";
import { styleForRelationType } from "../canvas/edgeStyles";

// Iets groter dan eerst (was 56×12) zodat de pijlpunt en
// diamond-shapes goed te onderscheiden zijn op detail-resolutie.
const W = 72;
const H = 18;
const MID = H / 2;
const PAD = 10;
const LEFT = PAD;
const RIGHT = W - PAD;

type MarkerKind =
  | "ae-composition"
  | "ae-aggregation"
  | "ae-open-triangle"
  | "ae-assignment-dot"
  | "ae-half-arrow"
  | "filled-arrow"
  | "open-arrow"
  | null;

function classifyMarker(m: EdgeMarker | string | undefined): MarkerKind {
  if (!m) return null;
  if (typeof m === "string") return m as MarkerKind;
  if (m.type === MarkerType.ArrowClosed) return "filled-arrow";
  if (m.type === MarkerType.Arrow) return "open-arrow";
  return null;
}

interface Props {
  typeId: string;
  attributes?: Record<string, unknown> | undefined;
  /** Flip horizontally (concept staat aan target-kant). */
  reversed?: boolean;
}

export function RelationGlyph({ typeId, attributes, reversed }: Props) {
  const base = styleForRelationType(typeId);
  // attribute-overrides — deze logica zit ook op canvas (RoutedEdge), maar
  // is niet ge-export. We hercoderen alleen de twee gevallen die het
  // visueel ander kunnen maken: Access.access_kind + Association.directed.
  let markerStart = base.markerStart;
  let markerEnd = base.markerEnd;
  if (typeId === "archimate.Access") {
    const kind = attributes?.access_kind;
    markerStart = undefined;
    markerEnd = undefined;
    if (kind === "read") markerStart = { type: MarkerType.Arrow, width: 18, height: 18 };
    else if (kind === "write") markerEnd = { type: MarkerType.Arrow, width: 18, height: 18 };
    else if (kind === "read-write") {
      markerStart = { type: MarkerType.Arrow, width: 18, height: 18 };
      markerEnd = { type: MarkerType.Arrow, width: 18, height: 18 };
    }
  } else if (typeId === "archimate.Association" && attributes?.directed === true) {
    markerEnd = "ae-half-arrow";
  }

  const startKind = classifyMarker(reversed ? markerEnd : markerStart);
  const endKind = classifyMarker(reversed ? markerStart : markerEnd);

  const stroke = base.baseColor;
  // Schaal de canvas-strokeWidth iets op zodat 'ie op de glyph-resolutie
  // niet als haarlijn vervaagt.
  const sw = ((base.style.strokeWidth as number | undefined) ?? 1.4) * 1.4;
  const dash = base.style.strokeDasharray as string | undefined;

  // Trim de lijn iets in zodat 'ie netjes aansluit op marker-shapes.
  const startInset = startKind ? 6 : 1;
  const endInset = endKind ? 6 : 1;

  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      aria-hidden
      style={{ display: "block", flexShrink: 0 }}
    >
      <title>{typeId}</title>
      <line
        x1={LEFT + startInset}
        y1={MID}
        x2={RIGHT - endInset}
        y2={MID}
        stroke={stroke}
        strokeWidth={sw}
        strokeDasharray={dash}
      />
      {startKind ? (
        <MarkerShape kind={startKind} x={LEFT} y={MID} side="left" color={stroke} />
      ) : null}
      {endKind ? (
        <MarkerShape kind={endKind} x={RIGHT} y={MID} side="right" color={stroke} />
      ) : null}
    </svg>
  );
}

function MarkerShape({
  kind,
  x,
  y,
  side,
  color,
}: {
  kind: MarkerKind;
  x: number;
  y: number;
  side: "left" | "right";
  color: string;
}) {
  // `dir` is +1 als het marker naar rechts wijst, -1 naar links.
  // - Aan de linkerkant van de lijn moet 'n pijlpunt naar links wijzen.
  // - Aan de rechterkant wijst 'ie naar rechts.
  const dir = side === "right" ? 1 : -1;
  switch (kind) {
    case "filled-arrow": {
      // Driehoek met punt op (x,y), basis ~10 pixels achter de punt.
      const tip = x;
      const base = x - dir * 10;
      const halfH = 5;
      return (
        <polygon
          points={`${tip},${y} ${base},${y - halfH} ${base},${y + halfH}`}
          fill={color}
          stroke={color}
          strokeWidth={0.8}
          strokeLinejoin="round"
        />
      );
    }
    case "open-arrow": {
      // Twee lijntjes ipv gevulde polygoon — chevron-stijl.
      const tip = x;
      const base = x - dir * 8;
      const halfH = 5;
      return (
        <g
          stroke={color}
          strokeWidth={1.4}
          fill="none"
          strokeLinejoin="round"
          strokeLinecap="round"
        >
          <line x1={tip} y1={y} x2={base} y2={y - halfH} />
          <line x1={tip} y1={y} x2={base} y2={y + halfH} />
        </g>
      );
    }
    case "ae-open-triangle": {
      const tip = x;
      const base = x - dir * 11;
      const halfH = 6;
      return (
        <polygon
          points={`${tip},${y} ${base},${y - halfH} ${base},${y + halfH}`}
          fill="white"
          stroke={color}
          strokeWidth={1.4}
          strokeLinejoin="round"
        />
      );
    }
    case "ae-composition": {
      // Filled diamond — punt op (x,y), uitsteek naar achteren.
      const tip = x;
      const back = x - dir * 12;
      const mid = (tip + back) / 2;
      const halfH = 5;
      return (
        <polygon
          points={`${tip},${y} ${mid},${y - halfH} ${back},${y} ${mid},${y + halfH}`}
          fill={color}
          stroke={color}
          strokeWidth={0.8}
          strokeLinejoin="round"
        />
      );
    }
    case "ae-aggregation": {
      // Hollow diamond.
      const tip = x;
      const back = x - dir * 12;
      const mid = (tip + back) / 2;
      const halfH = 5;
      return (
        <polygon
          points={`${tip},${y} ${mid},${y - halfH} ${back},${y} ${mid},${y + halfH}`}
          fill="white"
          stroke={color}
          strokeWidth={1.4}
          strokeLinejoin="round"
        />
      );
    }
    case "ae-assignment-dot": {
      return <circle cx={x} cy={y} r={3.5} fill={color} />;
    }
    case "ae-half-arrow": {
      // Half-arrow: alleen de bovenste lijn van een open-arrow.
      const tip = x;
      const base = x - dir * 8;
      const halfH = 5;
      return (
        <line
          x1={tip}
          y1={y}
          x2={base}
          y2={y - halfH}
          stroke={color}
          strokeWidth={1.4}
          strokeLinecap="round"
        />
      );
    }
    default:
      return null;
  }
}
